import { useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import posthog from "posthog-js";
import { useAppDispatch, useAppSelector } from "./stores/hooks/hooks";
import { setToken, clearAuth } from "./stores/slice/userAuthSlice";
import { getStorageName } from "./helpers/storageName/storageName";
import { AppRoutes } from "./routes/AppRoutes.tsx";
import { RevSpinner } from "@revmatics/catalyst";
import { fetchProfileData } from "./stores/thunks/profileThunk.tsx";
import { setCurrentBrand } from "./stores/slice/brandSlice.tsx";
import { signInWithSSOCookieThunk } from "./stores/thunks/userAuthThunk.tsx";
import { setCurrentOrganization } from "./stores/slice/organizationsSlice.tsx";
import {
  deleteRevmaticsCookie,
  getRevmaticsCookie,
} from "./helpers/cookie/revmaticsCookies.ts";
import { getCookieName } from "./helpers/cookieName/cookieName.ts";
import {
  redirectToSignIn,
  clearRedirectCounter,
  isRedirectCapped,
} from "./auth/redirectToSignIn";
import { AuthErrorState } from "./components/atoms/AuthErrorState";

// Helper function to read cookies
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

// Helper function to delete the SSO cookie on failure
const deleteCookie = (name: string) => {
  document.cookie = `${name}=; domain=.revmatics.ai; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure`;
};

export const AuthHandler = () => {
  const dispatch = useAppDispatch();

  const { token } = useAppSelector((state) => state.userAuth, shallowEqual);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    const bootstrapApp = async () => {
      const cookieBrand = getRevmaticsCookie(getCookieName.currentBrand);
      const cookieOrg = getRevmaticsCookie(getCookieName.currentOrganization);

      const storedToken = localStorage.getItem(getStorageName.accessToken);
      const storedBrand = localStorage.getItem(getStorageName.currentBrand);

      const storedOrganization = localStorage.getItem(
        getStorageName.currentOrganization,
      );

      const initialBrand = cookieBrand || storedBrand;
      const initialOrg = cookieOrg || storedOrganization;

      if (initialBrand) {
        dispatch(setCurrentBrand(initialBrand));
      }

      if (initialOrg) {
        dispatch(setCurrentOrganization(initialOrg));
      }

      if (storedToken) {
        // Path 1: User has an existing token in localStorage
        dispatch(setToken(storedToken));
        try {
          // Validate the token by fetching profile data
          const profileData = await dispatch(fetchProfileData()).unwrap();
          clearRedirectCounter();
          posthog.identify(profileData.email, { email: profileData.email });
          if (window.Appcues) {
            window.Appcues.identify(profileData.email, {
              email: profileData.email,
              name: `${profileData.first_name} ${profileData.last_name}`,
            });
          }
        } catch (error) {
          console.error(
            "Bootstrap failed: Invalid token or profile fetch failed.",
            error,
          );
          posthog.reset();
          dispatch(clearAuth());
          deleteRevmaticsCookie(getCookieName.currentBrand);
          deleteRevmaticsCookie(getCookieName.currentOrganization);
        }
      } else {
        // Path 2: No localStorage token. Try to use the SSO cookie.
        const ssoToken = getCookie("revmatics_sso_token");
        if (ssoToken) {
          try {
            // Validate the SSO token
            const ssoResponse = await dispatch(
              signInWithSSOCookieThunk(ssoToken),
            ).unwrap();
            dispatch(setToken(ssoResponse.access));
            const profileData = await dispatch(fetchProfileData()).unwrap();
            clearRedirectCounter();
            posthog.identify(profileData.email, { email: profileData.email });
          } catch (error) {
            console.error("SSO sign-in failed or profile fetch failed:", error);
            posthog.reset();
            dispatch(clearAuth());
            deleteCookie("revmatics_sso_token");
            deleteRevmaticsCookie(getCookieName.currentBrand);
            deleteRevmaticsCookie(getCookieName.currentOrganization);
          }
        }
      }

      // The bootstrap process is complete.
      setIsBootstrapping(false);
    };

    bootstrapApp();
  }, [dispatch]);

  if (isBootstrapping) {
    return (
      <div className="h-dvh">
        <RevSpinner size="xxl" centered color="primary" />
      </div>
    );
  }

  if (!token && isRedirectCapped()) {
    return (
      <AuthErrorState
        onSignIn={() => {
          clearRedirectCounter();
          redirectToSignIn();
        }}
      />
    );
  }

  return <AppRoutes token={token} />;
};
